import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../../shared/services/apiClient";
import { addDays } from "../../utils/dateUtils";
import styles from "../../styles/addInvoice.module.css";
import { useTipoCambio } from "../../../services/useTipoCambio";
import { useMultimoneda } from "../../../../../shared/hooks/useMultimoneda";
import { IvaSelector } from "../../../components/IvaSelector";

// ── Moneda + TC Card ──────────────────────────────────────────────────────────
const BADGE = {
  BCCR:     { color: "#4ade80", text: "BCCR" },
  MANUAL:   { color: "#fb923c", text: "Manual" },
  FALLBACK: { color: "#f87171", text: "Sin TC" },
};

const fmtN = (n) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function InvoiceTcRow({ moneda, onMonedaChange, tipoCambio, onTcChange, monto, loadingTC, tcFuente }) {
  const badge = BADGE[tcFuente] || BADGE.BCCR;
  const tc    = Number(tipoCambio) || 1;
  const m     = Number(monto) || 0;

  return (
    <div style={{
      marginTop: 14,
      border: "1px solid var(--border-card)",
      borderRadius: 10,
      overflow: "hidden",
      background: "rgba(255,255,255,0.02)",
    }}>
      {/* Cabecera */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "9px 14px",
        borderBottom: "1px solid var(--border-card)",
        background: "rgba(249,115,22,0.04)",
      }}>
        <i className="fa-solid fa-coins" style={{ color: "#f97316", fontSize: "0.7rem" }} />
        <span style={{ fontSize: "0.67rem", fontWeight: 700, color: "var(--text-muted)",
          letterSpacing: "0.6px", textTransform: "uppercase" }}>
          Moneda & Tipo de Cambio
        </span>
      </div>

      {/* Cuerpo */}
      <div style={{ padding: "13px 14px", display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Fila: segmentado CRC/USD + separador + TC */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

          {/* Segmentado */}
          <div style={{ display: "flex", background: "rgba(0,0,0,0.25)", borderRadius: 8, padding: 3 }}>
            {["CRC", "USD"].map((opt) => (
              <button key={opt} type="button" onClick={() => onMonedaChange(opt)}
                style={{
                  padding: "5px 20px", borderRadius: 6, fontSize: "0.83rem",
                  fontWeight: moneda === opt ? 700 : 400,
                  background: moneda === opt
                    ? opt === "USD" ? "rgba(96,165,250,0.2)" : "rgba(249,115,22,0.2)"
                    : "transparent",
                  border: `1px solid ${moneda === opt
                    ? opt === "USD" ? "rgba(96,165,250,0.45)" : "rgba(249,115,22,0.45)"
                    : "transparent"}`,
                  color: moneda === opt
                    ? opt === "USD" ? "#60a5fa" : "#f97316"
                    : "var(--text-muted)",
                  cursor: "pointer", transition: "all 0.15s",
                }}>
                {opt}
              </button>
            ))}
          </div>

          <div style={{ width: 1, height: 24, background: "var(--border-card)", flexShrink: 0 }} />

          {/* TC */}
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
              TC {moneda === "USD" ? "venta" : "ref"}:
            </span>
            {loadingTC
              ? <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#60a5fa", fontSize: "0.7rem" }} />
              : <span style={{
                  fontSize: "0.64rem", fontWeight: 700,
                  background: `${badge.color}18`, border: `1px solid ${badge.color}45`,
                  borderRadius: 4, padding: "2px 7px", color: badge.color, letterSpacing: "0.3px",
                }}>{badge.text}</span>
            }
            <div style={{
              display: "flex", alignItems: "center", gap: 4,
              background: "rgba(0,0,0,0.2)", border: "1px solid var(--border-card)",
              borderRadius: 7, padding: "4px 10px 4px 9px",
            }}>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontWeight: 600, lineHeight: 1 }}>₡</span>
              <input type="number" step="0.01" min="0.01" value={tc}
                onChange={(e) => onTcChange(parseFloat(e.target.value) || 1)}
                style={{
                  width: 82, background: "transparent", border: "none",
                  color: "var(--text-primary)", fontSize: "0.88rem",
                  fontFamily: "monospace", fontWeight: 600, outline: "none", padding: 0,
                }}
              />
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>/ USD</span>
            </div>
          </div>
        </div>

        {/* Equivalencia */}
        {m > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            background: "rgba(0,0,0,0.15)", borderRadius: 7, padding: "8px 12px",
          }}>
            <i className="fa-solid fa-arrow-right-arrow-left"
               style={{ color: "var(--text-muted)", fontSize: "0.62rem", flexShrink: 0 }} />
            {moneda === "USD" ? (
              <>
                <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ${fmtN(m)}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>USD equivale a</span>
                <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ₡{fmtN(m * tc)}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>al TC {tc.toFixed(2)}</span>
              </>
            ) : (
              <>
                <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ₡{fmtN(m)}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>equivale a</span>
                <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                  ${(m / tc).toFixed(2)} USD
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>al TC {tc.toFixed(2)}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** True si el nombre del rubro contiene "agua" */
function esAgua(nombre = "") {
  return /agua/i.test(nombre);
}

/** Mensaje de error inline */
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        color: "#f87171",
        fontSize: "0.77rem",
        marginTop: 4,
        fontWeight: 500,
      }}
    >
      <i
        className="fa-solid fa-circle-exclamation"
        style={{ fontSize: "0.72rem" }}
      />
      {msg}
    </div>
  );
}

export default function InvoiceDetailsBody({
  invoiceData,
  updateField,
  errors = {},
  onIsAguaChange,
  isAgua,
}) {
  const { t, i18n } = useTranslation();
  const multimoneda = useMultimoneda();
  const [invoiceNumber, setInvoiceNumber] = useState("...");
  const [rubros, setRubros] = useState([]);
  const [ultimaMedida, setUltimaMedida] = useState(null);
  const [cargandoMedida, setCargandoMedida] = useState(false);
  const { tipoCambio, loading: loadingTC } = useTipoCambio();

  // Cargar TC_compra desde BCCR solo si multimoneda está activa.
  // invoiceData.tipo_cambio en deps para re-disparar cuando el form se resetea a 1.
  useEffect(() => {
    if (!multimoneda) return;
    if (tipoCambio?.tipo_compra && (!invoiceData.tipo_cambio || invoiceData.tipo_cambio === 1)) {
      updateField("tipo_cambio", Number(tipoCambio.tipo_compra));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio, multimoneda, invoiceData.tipo_cambio]);

  /** Genera el concepto automático basado en el nombre del rubro y la fecha de emisión */
  function conceptoAutoFromRubro(nombre, fechaEmision) {
    let fecha;
    if (fechaEmision) {
      const [year, month] = fechaEmision.split("-").map(Number);
      fecha = { month: month - 1, year };
    } else {
      const now = new Date();
      fecha = { month: now.getMonth(), year: now.getFullYear() };
    }
    const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-CR";
    const date = new Date(fecha.year, fecha.month, 1);
    const mes = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
    return `${nombre} — ${mes} ${fecha.year}`;
  }

  // ── Rubro actualmente seleccionado (objeto completo) ────────────────
  const rubroActual =
    rubros.find((r) => r.id === Number(invoiceData.rubro)) ?? null;
  const isAguaFlag = rubroActual ? esAgua(rubroActual.nombre) : false;

  async function fetchProximoNumero() {
    try {
      const res = await apiFetch("/api/finance/factura/proximo-numero/");
      const data = await res.json();
      setInvoiceNumber(data.proximo ?? "—");
    } catch {
      setInvoiceNumber("—");
    }
  }

  useEffect(() => {
    if (!invoiceData.filial || !isAgua) {
      setUltimaMedida(null);
      return;
    }

    setCargandoMedida(true);
    apiFetch(
      `/api/finance/factura/?filial=${invoiceData.filial}&cuenta_ingreso=${invoiceData.rubro}&ordering=-fecha_emision&page_size=1`,
    )
      .then((r) => r.json())
      .then((data) => {
        const facturas = data.results ?? [];
        const ultima = facturas.find(
          (f) => f.medida_actual !== null && f.medida_actual !== undefined,
        );
        if (ultima) {
          const medida = Number(ultima.medida_actual);
          setUltimaMedida(medida);
          updateField("medida_anterior", medida);
        } else {
          setUltimaMedida(null);
        }
      })
      .catch(() => setUltimaMedida(null))
      .finally(() => setCargandoMedida(false));
  }, [invoiceData.filial, isAgua]);

  // ── Cargar rubros al montar ─────────────────────────────────────────
  useEffect(() => {
    fetchProximoNumero();

    apiFetch("/api/finance/catalogo/detalle/?tipo=INGRESO")
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.results ?? []);
        setRubros(list);

        if (list.length === 0) return;

        const primero = list[0];

        const yaExiste =
          invoiceData.rubro &&
          list.some((r) => r.id === Number(invoiceData.rubro));
        if (!yaExiste) {
          updateField("rubro", primero.id);
          updateField("cuenta_ingreso", primero.id);
          if (!invoiceData.concepto) {
            updateField(
              "concepto",
              conceptoAutoFromRubro(primero.nombre, invoiceData.fecha_emision),
            );
          }
          if (onIsAguaChange) onIsAguaChange(esAgua(primero.nombre));
        } else {
          const actual = list.find((r) => r.id === Number(invoiceData.rubro));
          if (onIsAguaChange && actual) onIsAguaChange(esAgua(actual.nombre));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (invoiceData.rubro === null && rubros.length > 0) {
      const primero = rubros[0];
      updateField("rubro", primero.id);
      updateField("cuenta_ingreso", primero.id);
      updateField(
        "concepto",
        conceptoAutoFromRubro(primero.nombre, invoiceData.fecha_emision),
      );
      if (onIsAguaChange) onIsAguaChange(esAgua(primero.nombre));
      fetchProximoNumero();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData.rubro, rubros.length]);

  useEffect(() => {
    if (onIsAguaChange && rubroActual) {
      onIsAguaChange(isAguaFlag);
    }
  }, [isAguaFlag]);

  // ── Cambio de rubro ─────────────────────────────────────────────────
  function handleRubroChange(id) {
    const nuevo = rubros.find((r) => r.id === Number(id));
    if (!nuevo) return;

    updateField("rubro", nuevo.id);
    updateField("cuenta_ingreso", nuevo.id);
    updateField(
      "concepto",
      conceptoAutoFromRubro(nuevo.nombre, invoiceData.fecha_emision),
    );

    if (!esAgua(nuevo.nombre)) {
      updateField("medida_anterior", "");
      updateField("medida_actual", "");
    }
  }

  // ── Fecha de emisión ─────────────────────────────────────────────────
  function handleEmissionDate(date) {
    updateField("fecha_emision", date);
    updateField("fecha_vencimiento", date ? addDays(date, 5) : "");
    if (rubroActual) {
      updateField("concepto", conceptoAutoFromRubro(rubroActual.nombre, date));
    }
  }

  // ── Consumo agua preview ─────────────────────────────────────────────
  const consumo =
    isAguaFlag &&
    invoiceData.medida_anterior !== "" &&
    invoiceData.medida_actual !== ""
      ? (
          Number(invoiceData.medida_actual) -
          Number(invoiceData.medida_anterior)
        ).toFixed(2)
      : null;

  const consumoNegativo = consumo !== null && Number(consumo) < 0;

  return (
    <div className={styles["invoice-details-body"]}>
      {/* ── N° Factura + Rubro ──────────────────────────────────── */}
      <div className="row">
        <div className="col">
          <label>{t("invoices.numFactura")}</label>
          <input
            className="form-control"
            value={invoiceNumber}
            disabled
            title={t("invoices.numAsignadoAuto")}
          />
        </div>

        <div className="col">
          <label>
            {t("invoices.rubro")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <select
            className="form-select"
            value={invoiceData.rubro ?? ""}
            onChange={(e) => handleRubroChange(e.target.value)}
            style={errors.rubro ? { borderColor: "#ef4444" } : undefined}
          >
            {rubros.length === 0 && (
              <option value="">{t("invoices.cargandoRubros")}</option>
            )}
            {rubros.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nombre}
              </option>
            ))}
          </select>
          <FieldError msg={errors.rubro} />
        </div>
      </div>

      {/* ── Medidas de agua ─────────────────────────────────────────── */}
      {isAguaFlag && (
        <div
          style={{
            background: "rgba(21,101,192,0.08)",
            border: "1px solid rgba(21,101,192,0.25)",
            borderLeft: "3px solid #1565C0",
            borderRadius: 6,
            padding: "12px 14px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginBottom: 10,
              color: "#90CAF9",
              fontWeight: 600,
              fontSize: "0.83rem",
            }}
          >
            <i className="fa-solid fa-droplet" />
            {t("invoices.lecturasAgua")}
          </div>

          <div className="row">
            <div className="col">
              <label>
                {t("invoices.medidaAnterior")}
                <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder={
                  cargandoMedida ? t("invoices.buscandoLectura") : t("invoices.ejMedida1")
                }
                value={invoiceData.medida_anterior}
                onChange={(e) => updateField("medida_anterior", e.target.value)}
                readOnly={ultimaMedida !== null}
                style={
                  ultimaMedida !== null
                    ? {
                        background: "rgba(21,101,192,0.08)",
                        borderColor: "#1565C0",
                        color: "#90CAF9",
                        cursor: "not-allowed",
                      }
                    : undefined
                }
              />
              <FieldError msg={errors.medida_anterior} />
              {ultimaMedida !== null && (
                <div style={{ marginTop: 4, fontSize: "0.74rem", color: "#90CAF9" }}>
                  <i className="fa-solid fa-lock" style={{ marginRight: 5 }} />
                  {t("invoices.medidaAutoAnterior")}
                </div>
              )}
              {cargandoMedida && (
                <i
                  className="fa-solid fa-circle-notch fa-spin"
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#60a5fa",
                    fontSize: "0.8rem",
                  }}
                />
              )}
            </div>

            <div className="col">
              <label>
                {t("invoices.medidaActual")}
                <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder={t("invoices.ejMedida2")}
                value={invoiceData.medida_actual}
                onChange={(e) => updateField("medida_actual", e.target.value)}
                style={
                  errors.medida_actual || consumoNegativo
                    ? {
                        borderColor: "#ef4444",
                        boxShadow: "0 0 0 2px rgba(239,68,68,0.2)",
                      }
                    : undefined
                }
              />
              <FieldError msg={errors.medida_actual} />
            </div>
          </div>

          {consumo !== null && (
            <div
              style={{
                marginTop: 8,
                fontSize: "0.82rem",
                fontWeight: 600,
                color: consumoNegativo ? "#f87171" : "#81C784",
              }}
            >
              {consumoNegativo
                ? t("invoices.medidaError")
                : `💧 ${t("invoices.consumo")}: ${consumo} m³`}
            </div>
          )}
          <div style={{ marginTop: 6, fontSize: "0.74rem", color: "var(--text-muted)" }}>
            {t("invoices.medidaObligatoria")}
          </div>
        </div>
      )}

      {/* ── Concepto (auto, editable) ────────────────────────────── */}
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {t("cxp.concepto")}
          <span
            style={{
              fontSize: "0.68rem",
              color: "var(--text-secondary)",
              background: "var(--bg-card)",
              border: "1px solid var(--border-card)",
              borderRadius: 4,
              padding: "1px 6px",
              fontWeight: 400,
            }}
          >
            {t("cxp.autoBadge")}
          </span>
        </label>
        <input
          className="form-control"
          value={invoiceData.concepto}
          onChange={(e) => updateField("concepto", e.target.value)}
          placeholder={t("invoices.conceptoAutoPlaceholder")}
        />
        <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 3 }}>
          {t("invoices.conceptoHint")}
        </div>
      </div>

      {/* ── Fechas ──────────────────────────────────────────────── */}
      <div className="row">
        <div className="col">
          <label>
            {t("invoices.fechaEmision")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={invoiceData.fecha_emision}
            onChange={(e) => handleEmissionDate(e.target.value)}
            style={errors.fecha_emision ? { borderColor: "#ef4444" } : undefined}
          />
          <FieldError msg={errors.fecha_emision} />
        </div>

        <div className="col">
          <label>
            {t("invoices.fechaVencimiento")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={invoiceData.fecha_vencimiento}
            onChange={(e) => updateField("fecha_vencimiento", e.target.value)}
            style={errors.fecha_vencimiento ? { borderColor: "#ef4444" } : undefined}
          />
          <FieldError msg={errors.fecha_vencimiento} />
        </div>
      </div>

      {/* ── Monto ───────────────────────────────────────────────── */}
      <label>
        {t("common.monto")} <span style={{ color: "#f87171" }}>*</span>
        {multimoneda && invoiceData.moneda === "USD" && (
          <span style={{ marginLeft: 6, fontSize: "0.75rem", color: "var(--text-muted)" }}>
            (en USD)
          </span>
        )}
      </label>
      <input
        type="number"
        className="form-control"
        value={invoiceData.monto || ""}
        placeholder="0.00"
        onChange={(e) => updateField("monto", Number(e.target.value))}
        style={errors.monto ? { borderColor: "#ef4444" } : undefined}
      />
      <FieldError msg={errors.monto} />
      <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 3 }}>
        {invoiceData.iva_incluido && invoiceData.iva_modo === "INCLUIDO"
          ? <>Ingresá el monto <strong>con IVA incluido</strong> (total). El sistema desglosa la base y el IVA.</>
          : <>Ingresá el monto <strong>sin IVA</strong> (base). El sistema calcula el IVA y el total.</>}
      </div>

      {/* ── IVA ──────────────────────────────────────────────────── */}
      <IvaSelector
        ivaIncluido={invoiceData.iva_incluido}
        tasaIva={invoiceData.tasa_iva}
        onIvaIncluidoChange={(v) => updateField("iva_incluido", v)}
        onTasaChange={(v) => updateField("tasa_iva", v)}
        base={invoiceData.monto || 0}
        moneda={invoiceData.moneda || "CRC"}
        modo={invoiceData.iva_modo || "SUMAR"}
        onModoChange={(v) => updateField("iva_modo", v)}
      />

      {/* ── Moneda + TC — solo en modo multimoneda ──────────────── */}
      {multimoneda && (
        <InvoiceTcRow
          moneda={invoiceData.moneda || "CRC"}
          onMonedaChange={(m) => {
            updateField("moneda", m);
            // Al cambiar a USD cargar TC compra; al volver a CRC resetear a 1
            if (m === "USD" && tipoCambio?.tipo_compra) {
              updateField("tipo_cambio", Number(tipoCambio.tipo_compra));
            } else if (m === "CRC") {
              updateField("tipo_cambio", 1);
            }
          }}
          tipoCambio={invoiceData.tipo_cambio || 1}
          onTcChange={(tc) => updateField("tipo_cambio", tc)}
          monto={invoiceData.monto || 0}
          loadingTC={loadingTC}
          tcFuente={tipoCambio?.fuente || "BCCR"}
        />
      )}
    </div>
  );
}
